import React, { useState, useEffect } from 'react';
import { Layout, Card, Form, Input, Button, Avatar, message, Descriptions, Modal, Space, Radio } from 'antd';
import { UserOutlined, ArrowLeftOutlined, LockOutlined, SafetyOutlined, MobileOutlined, CalendarOutlined, MessageOutlined, CheckOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { getUserInfo, updateUserInfo, changePassword, changePasswordByCode, sendChangePasswordCode, logout, sendBindPhoneCode, bindPhone } from '../../api/auth';
import { passwordValidator, confirmPasswordValidator, phoneValidator, codeValidator } from '../../utils/validate';
import { extractSendCodeMockCode } from '../../utils/sendCodeMock';
import './index.css';

const { Header, Content } = Layout;

// 预设头像列表：人物、动物（大耳可爱风）、二次元插画等，用户只能从中选择
const PRESET_AVATARS = [
  // 二次元 / 插画风格 (lorelei)
  'https://api.dicebear.com/9.x/lorelei/svg?seed=anime1',
  'https://api.dicebear.com/9.x/lorelei/svg?seed=anime2',
  'https://api.dicebear.com/9.x/lorelei/svg?seed=anime3',
  'https://api.dicebear.com/9.x/lorelei/svg?seed=anime4',
  // 动物 / 可爱大耳风格 (big-ears)
  'https://api.dicebear.com/9.x/big-ears/svg?seed=cat',
  'https://api.dicebear.com/9.x/big-ears/svg?seed=dog',
  'https://api.dicebear.com/9.x/big-ears/svg?seed=bear',
  'https://api.dicebear.com/9.x/big-ears/svg?seed=rabbit',
  'https://api.dicebear.com/9.x/big-ears/svg?seed=fox',
  'https://api.dicebear.com/9.x/big-ears/svg?seed=panda',
  // 人物风格 (avataaars)
  'https://api.dicebear.com/9.x/avataaars/svg?seed=person1',
  'https://api.dicebear.com/9.x/avataaars/svg?seed=person2',
  'https://api.dicebear.com/9.x/avataaars/svg?seed=person3',
  'https://api.dicebear.com/9.x/avataaars/svg?seed=person4',
];

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [userInfo, setUserInfo] = useState<any>(null);
  const [bindPhoneModalVisible, setBindPhoneModalVisible] = useState(false);
  const [bindPhoneMode, setBindPhoneMode] = useState<'bind' | 'change'>('bind');
  const [bindPhoneForm] = Form.useForm();
  const [bindPhoneLoading, setBindPhoneLoading] = useState(false);
  const [bindCodeLoading, setBindCodeLoading] = useState(false);
  const [bindCodeCooldown, setBindCodeCooldown] = useState(0);
  const [passwordChangeMethod, setPasswordChangeMethod] = useState<'oldPassword' | 'code'>('oldPassword');
  const [changeCodeCooldown, setChangeCodeCooldown] = useState(0);
  const [changeCodeLoading, setChangeCodeLoading] = useState(false);
  const { user, updateUser, logout: logoutStore } = useAuthStore();

  const SEND_CODE_COOLDOWN = 60;

  // 加载用户信息
  useEffect(() => {
    loadUserInfo();
  }, []);

  const loadUserInfo = async () => {
    try {
      const response = await getUserInfo();
      if (response.code === 200 && response.data) {
        const data = response.data;
        setUserInfo(data);
        form.setFieldsValue({
          nickname: data.nickname,
          avatar: data.avatar,
        });
        updateUser(data);
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || '获取用户信息失败');
    }
  };

  // 更新用户信息（含预设头像）
  const onFinish = async (values: any) => {
    setLoading(true);
    try {
      const response = await updateUserInfo({
        nickname: values.nickname,
        avatar: values.avatar || undefined,
      });
      
      if (response.code === 200 && response.data) {
        updateUser(response.data);
        setUserInfo(response.data);
        message.success('保存成功');
      } else {
        message.error(response.message || '保存失败');
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || '保存失败');
    } finally {
      setLoading(false);
    }
  };

  // 发送修改密码验证码
  const handleSendChangePasswordCode = async () => {
    const phone = displayUser?.phone;
    if (!phone) {
      message.warning('请先绑定手机号后再使用验证码修改密码');
      return;
    }
    setChangeCodeLoading(true);
    try {
      const res = await sendChangePasswordCode(phone);
      if (res.code === 200) {
        message.success('验证码已发送');
        const mockCode = extractSendCodeMockCode(res);
        if (mockCode) {
          Modal.info({
            title: '测试验证码（未真实发送短信）',
            content: (
              <div>
                <p className="text-gray-600 mb-2">当前为测试环境，短信未真实发送。</p>
                <p className="text-lg font-mono font-semibold text-blue-600">验证码：{mockCode}</p>
              </div>
            ),
            okText: '知道了',
          });
        }
        let n = SEND_CODE_COOLDOWN;
        setChangeCodeCooldown(n);
        const t = setInterval(() => {
          n -= 1;
          setChangeCodeCooldown(n);
          if (n <= 0) clearInterval(t);
        }, 1000);
      } else {
        message.error(res.message || '发送失败');
      }
    } catch (e: any) {
      message.error(e.response?.data?.message || e.message || '发送失败');
    } finally {
      setChangeCodeLoading(false);
    }
  };

  // 修改密码（旧密码 或 手机验证码）
  const onPasswordFinish = async (values: any) => {
    if (passwordChangeMethod === 'code' && !displayUser?.phone) {
      message.warning('请先绑定手机号后再使用验证码修改密码');
      return;
    }
    setPasswordLoading(true);
    try {
      if (passwordChangeMethod === 'code') {
        const response = await changePasswordByCode({
          phone: displayUser!.phone,
          code: values.code,
          newPassword: values.newPassword,
        });
        if (response.code === 200) {
          message.success('密码修改成功，请重新登录');
          setPasswordModalVisible(false);
          passwordForm.resetFields();
          setTimeout(() => handleLogout(), 1500);
        } else {
          message.error(response.message || '密码修改失败');
        }
      } else {
        const response = await changePassword({
          oldPassword: values.oldPassword,
          newPassword: values.newPassword,
        });
        if (response.code === 200) {
          message.success('密码修改成功，请重新登录');
          setPasswordModalVisible(false);
          passwordForm.resetFields();
          setTimeout(() => handleLogout(), 1500);
        } else {
          message.error(response.message || '密码修改失败');
        }
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || '密码修改失败');
    } finally {
      setPasswordLoading(false);
    }
  };

  // 退出登录
  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      // 即使API失败也清除本地状态
    } finally {
      logoutStore();
      navigate('/login');
    }
  };

  const handleSendBindCode = async () => {
    try {
      await bindPhoneForm.validateFields(['phone']);
    } catch {
      return;
    }
    const phone = bindPhoneForm.getFieldValue('phone');
    setBindCodeLoading(true);
    try {
      const res = await sendBindPhoneCode(phone);
      if (res.code === 200) {
        message.success('验证码已发送');
        const mockCode = extractSendCodeMockCode(res);
        if (mockCode) {
          Modal.info({
            title: '测试验证码（未真实发送短信）',
            content: (
              <div>
                <p className="text-gray-600 mb-2">当前为测试环境，短信未真实发送。</p>
                <p className="text-lg font-mono font-semibold text-blue-600">验证码：{mockCode}</p>
              </div>
            ),
            okText: '知道了',
          });
        }
        let n = SEND_CODE_COOLDOWN;
        setBindCodeCooldown(n);
        const t = setInterval(() => {
          n -= 1;
          setBindCodeCooldown(n);
          if (n <= 0) clearInterval(t);
        }, 1000);
      } else {
        message.error(res.message || '发送失败');
      }
    } catch (e: any) {
      const msg = e.response?.data?.message || e.message || '发送失败';
      message.error(msg);
    } finally {
      setBindCodeLoading(false);
    }
  };

  const onBindPhoneFinish = async (values: any) => {
    setBindPhoneLoading(true);
    try {
      const res = await bindPhone({ phone: values.phone, code: values.code });
      if (res.code === 200 && res.data) {
        message.success(bindPhoneMode === 'change' ? '手机号更换成功' : '手机号绑定成功');
        updateUser(res.data);
        setUserInfo(res.data);
        setBindPhoneModalVisible(false);
        bindPhoneForm.resetFields();
      } else {
        message.error(res.message || (bindPhoneMode === 'change' ? '更换失败' : '绑定失败'));
      }
    } catch (e: any) {
      message.error(e.response?.data?.message || e.message || (bindPhoneMode === 'change' ? '更换失败' : '绑定失败'));
    } finally {
      setBindPhoneLoading(false);
    }
  };

  const displayUser = userInfo || user;
  const selectedAvatar = Form.useWatch('avatar', form) ?? displayUser?.avatar;

  return (
    <Layout className="min-h-screen bg-gray-50">
      <Header className="bg-white shadow-sm px-6 flex items-center justify-between sticky top-0 z-10">
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/')}
          className="text-base"
        >
          返回
        </Button>
        <div className="text-lg font-semibold text-gray-800">个人中心</div>
        <div style={{ width: 80 }}></div>
      </Header>
      
      <Content className="px-4 py-6 max-w-4xl lg:max-w-6xl mx-auto w-full">
        {/* 用户信息卡片 */}
        <Card className="mb-6 shadow-sm">
          <div className="text-center mb-6">
            <Avatar 
              size={100} 
              src={selectedAvatar}
              icon={!selectedAvatar ? <UserOutlined /> : undefined}
              className="border-4 border-white shadow-lg"
            />
            <h2 className="mt-4 text-xl font-semibold text-gray-800">
              {displayUser?.nickname || displayUser?.username || '用户'}
            </h2>
          </div>

          <Descriptions column={1} bordered className="mb-6">
            {/* 邮箱信息暂时隐藏 */}
            <Descriptions.Item 
              label={
                <span className="flex items-center">
                  <MobileOutlined className="mr-2" />
                  手机号
                </span>
              }
            >
              {displayUser?.phone ? (
                <span className="flex items-center gap-2">
                  {displayUser.phone}
                  <Button type="link" size="small" onClick={() => { setBindPhoneMode('change'); setBindPhoneModalVisible(true); }} className="p-0">
                    更换手机号
                  </Button>
                </span>
              ) : (
                <Button type="link" size="small" onClick={() => { setBindPhoneMode('bind'); setBindPhoneModalVisible(true); }} className="p-0">
                  绑定手机号
                </Button>
              )}
            </Descriptions.Item>
            <Descriptions.Item 
              label={
                <span className="flex items-center">
                  <CalendarOutlined className="mr-2" />
                  注册时间
                </span>
              }
            >
              {displayUser?.registerTime 
                ? new Date(displayUser.registerTime).toLocaleString('zh-CN')
                : '未知'}
            </Descriptions.Item>
            <Descriptions.Item 
              label={
                <span className="flex items-center">
                  <MessageOutlined className="mr-2" />
                  剩余对话次数
                </span>
              }
            >
              <span className="text-blue-600 font-semibold">
                无限次
              </span>
            </Descriptions.Item>
          </Descriptions>
        </Card>

        {/* 修改信息卡片 */}
        <Card title="修改信息" className="mb-6 shadow-sm">
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            initialValues={{
              nickname: displayUser?.nickname || '',
              avatar: displayUser?.avatar || undefined,
            }}
          >
            <Form.Item label="选择头像" name="avatar">
              <div className="flex flex-wrap gap-3 justify-center py-2">
                {PRESET_AVATARS.map((url) => (
                  <div
                    key={url}
                    onClick={() => form.setFieldsValue({ avatar: url })}
                    className={`relative cursor-pointer rounded-full p-1 border-2 transition-all hover:border-orange-400 ${
                      selectedAvatar === url ? 'border-orange-500 ring-2 ring-orange-200' : 'border-gray-200'
                    }`}
                  >
                    <Avatar size={56} src={url} />
                    {selectedAvatar === url && (
                      <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/20 pointer-events-none">
                        <CheckOutlined className="text-white text-lg" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Form.Item>

            <Form.Item
              label="用户名"
              name="nickname"
              rules={[
                { required: true, message: '请输入用户名!' },
                { min: 2, max: 20, message: '用户名为2-20个字符' },
              ]}
            >
              <Input placeholder="请输入用户名" />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" loading={loading}>
                  保存修改
                </Button>
                <Button 
                  icon={<LockOutlined />}
                  onClick={() => setPasswordModalVisible(true)}
                >
                  修改密码
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>

        {/* 账号安全卡片 */}
        <Card title="账号安全" className="mb-6 shadow-sm">
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded">
              <div className="flex items-center">
                <SafetyOutlined className="text-blue-500 text-xl mr-3" />
                <div>
                  <div className="font-medium">登录状态</div>
                  <div className="text-sm text-gray-500">当前设备已登录</div>
                </div>
              </div>
              <span className="text-green-600">正常</span>
            </div>
            
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded">
              <div className="flex items-center">
                <MobileOutlined className="text-blue-500 text-xl mr-3" />
                <div>
                  <div className="font-medium">登录设备</div>
                  <div className="text-sm text-gray-500">
                    {navigator.userAgent.includes('Windows') ? 'Windows' : 
                     navigator.userAgent.includes('Mac') ? 'Mac' : 
                     navigator.userAgent.includes('Linux') ? 'Linux' : '未知设备'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* 退出登录 */}
        <Card className="shadow-sm">
          <Button 
            danger 
            block 
            size="large"
            onClick={handleLogout}
          >
            退出登录
          </Button>
        </Card>
      </Content>

      {/* 绑定/更换手机号弹窗 */}
      <Modal
        title={bindPhoneMode === 'change' ? '更换手机号' : '绑定手机号'}
        open={bindPhoneModalVisible}
        onCancel={() => {
          setBindPhoneModalVisible(false);
          bindPhoneForm.resetFields();
        }}
        footer={null}
        width={400}
      >
        <Form
          form={bindPhoneForm}
          layout="vertical"
          onFinish={onBindPhoneFinish}
          className="mt-4"
        >
          <Form.Item
            name="phone"
            label={bindPhoneMode === 'change' ? '新手机号' : '手机号'}
            rules={[{ validator: phoneValidator }]}
          >
            <Input prefix={<MobileOutlined />} placeholder={bindPhoneMode === 'change' ? '请输入新手机号' : '请输入手机号'} />
          </Form.Item>
          <Form.Item name="code" label="验证码" rules={[{ validator: codeValidator }]}>
            <div className="flex gap-2">
              <Input placeholder="请输入6位验证码" maxLength={6} />
              <Button
                type="primary"
                loading={bindCodeLoading}
                disabled={bindCodeCooldown > 0}
                onClick={handleSendBindCode}
              >
                {bindCodeCooldown > 0 ? `${bindCodeCooldown}s 后重发` : '获取验证码'}
              </Button>
            </div>
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={bindPhoneLoading} block>
              {bindPhoneMode === 'change' ? '确认更换' : '确认绑定'}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* 修改密码弹窗 */}
      <Modal
        title="修改密码"
        open={passwordModalVisible}
        onCancel={() => {
          setPasswordModalVisible(false);
          passwordForm.resetFields();
          setPasswordChangeMethod('oldPassword');
        }}
        footer={null}
        width={500}
      >
        <Form
          form={passwordForm}
          layout="vertical"
          onFinish={onPasswordFinish}
          className="mt-4"
        >
          <Form.Item label="验证方式">
            <Radio.Group
              optionType="button"
              buttonStyle="solid"
              value={passwordChangeMethod}
              onChange={(e) => setPasswordChangeMethod(e.target.value)}
              options={[
                { label: '旧密码', value: 'oldPassword' },
                { label: '手机验证码', value: 'code' },
              ]}
            />
          </Form.Item>

          {passwordChangeMethod === 'oldPassword' && (
            <Form.Item
              label="旧密码"
              name="oldPassword"
              rules={[{ required: true, message: '请输入旧密码' }]}
            >
              <Input.Password prefix={<LockOutlined />} placeholder="请输入旧密码" />
            </Form.Item>
          )}

          {passwordChangeMethod === 'code' && (
            <>
              <Form.Item label="绑定手机号">
                <Input prefix={<MobileOutlined />} value={displayUser?.phone || ''} disabled placeholder="请先绑定手机号" />
              </Form.Item>
              <Form.Item
                name="code"
                label="验证码"
                rules={[{ required: true, message: '请获取并输入验证码' }, { validator: codeValidator }]}
              >
                <div className="flex gap-2">
                  <Input placeholder="请输入6位验证码" maxLength={6} />
                  <Button
                    type="primary"
                    loading={changeCodeLoading}
                    disabled={changeCodeCooldown > 0 || !displayUser?.phone}
                    onClick={handleSendChangePasswordCode}
                  >
                    {changeCodeCooldown > 0 ? `${changeCodeCooldown}s 后重发` : '获取验证码'}
                  </Button>
                </div>
              </Form.Item>
            </>
          )}

          <Form.Item
            label="新密码"
            name="newPassword"
            rules={[{ validator: passwordValidator }]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="请输入新密码（6-20位字母+数字）" />
          </Form.Item>

          <Form.Item
            label="确认新密码"
            name="confirmPassword"
            dependencies={['newPassword']}
            rules={[{ required: true, message: '请确认新密码' }, confirmPasswordValidator('newPassword')]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="请再次输入新密码" />
          </Form.Item>

          <Form.Item>
            <Space className="w-full justify-end">
              <Button onClick={() => {
                setPasswordModalVisible(false);
                passwordForm.resetFields();
                setPasswordChangeMethod('oldPassword');
              }}>
                取消
              </Button>
              <Button type="primary" htmlType="submit" loading={passwordLoading}>
                确认修改
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
};

export default Profile;
